import { withProperty } from '../PropertyProvider';
import { Link } from 'react-router-dom';
import { translate } from 'App/Internationalization';
import { withTheme } from '../../../shared/ThemeProvider';
import Loading from '../../../shared/Loading';
import React from 'react';
import paths from '../../../config/local/paths';

const SCOPED_NAV_ITEMS = [
	{
		key: 'market',
		label: translate(
			'homeowner.pages.properties.scoped.submenu.market_valuation'
		),
		path: paths.app.homeowner.properties.scoped.marketValuation,
	},
	{
		key: 'finances',
		label: translate('homeowner.pages.properties.scoped.submenu.finances'),
		path: paths.app.homeowner.properties.scoped.finances,
	},
	{
		key: 'details',
		label: translate('homeowner.pages.properties.scoped.submenu.details'),
		path: paths.app.homeowner.properties.scoped.details,
	},
	{
		key: 'documents',
		label: translate('homeowner.pages.properties.scoped.submenu.documents'),
		path: paths.app.homeowner.properties.scoped.documents,
	},
	{
		key: 'appliances',
		label: translate(
			'homeowner.pages.properties.scoped.submenu.appliances'
		),
		path: paths.app.homeowner.properties.scoped.appliances,
	},
	{
		key: 'team',
		label: translate('homeowner.pages.properties.scoped.submenu.team'),
		path: paths.app.homeowner.properties.scoped.team,
	},
];

/**
 * @typedef {{
 *  key: string,
 *  label: string,
 *  path: string
 * }} ScopedNavItem
 */

/**
 * @typedef {{
 *  location: { pathname: string },
 *  match: { params: { propertyId?: string } },
 *  property: {
 *   propertyList?: { id?: string }[],
 *   selectedProperty?: { id?: string } | null,
 *   setSelectedProperty: (propertyId: string) => Promise<void>
 *  },
 *  theme?: { agentId?: string | null, teamList?: unknown[] },
 *  children?: React.ReactNode
 * }} PropertyScopedLayoutProps
 */

/** @extends {React.Component<PropertyScopedLayoutProps, {isSyncingProperty: boolean}>} */
class PropertyScopedLayout extends React.Component {
	/** @param {PropertyScopedLayoutProps} props */
	constructor(props) {
		super(props);

		this.state = {
			isSyncingProperty: false,
		};
	}

	async componentDidMount() {
		await this.syncSelectedProperty();
	}

	/**
	 * @param {PropertyScopedLayoutProps} prevProps
	 * @returns {Promise<void>}
	 */
	async componentDidUpdate(prevProps) {
		if (
			prevProps.match.params.propertyId !==
			this.props.match.params.propertyId
		) {
			await this.syncSelectedProperty();
		}
	}

	async syncSelectedProperty() {
		const propertyId = this.props.match.params.propertyId;

		if (!propertyId) {
			return;
		}

		if (
			this.props.property.propertyList?.length &&
			!this.props.property.propertyList.some(
				(property) => property.id === propertyId
			)
		) {
			return;
		}

		if (this.props.property.selectedProperty?.id === propertyId) {
			return;
		}

		this.setState({ isSyncingProperty: true });

		try {
			await this.props.property.setSelectedProperty(propertyId);
		} finally {
			this.setState({ isSyncingProperty: false });
		}
	}

	/**
	 * @param {string} itemPath
	 * @returns {boolean}
	 */
	isActive(itemPath) {
		const pathname = this.props.location.pathname;
		const resolvedPath = itemPath.replace(
			':propertyId',
			this.props.match.params.propertyId
		);

		return pathname === resolvedPath;
	}

	/**
	 * @returns {ScopedNavItem[]}
	 */
	getNavItems() {
		const hasTeam = Boolean(
			this.props.theme?.agentId && this.props.theme?.teamList?.length
		);

		return hasTeam
			? SCOPED_NAV_ITEMS
			: SCOPED_NAV_ITEMS.filter((item) => item.key !== 'team');
	}

	/**
	 * @param {ScopedNavItem} item
	 * @param {string} propertyId
	 * @returns {JSX.Element}
	 */
	renderNavLink(item, propertyId) {
		const isActive = this.isActive(item.path);

		return (
			<Link
				className={
					isActive
						? 'inline-flex items-center rounded-md bg-theme-light text-black font-semibold px-4 py-2 border-b-2 border-theme no-underline'
						: 'inline-flex items-center rounded-md bg-transparent text-grey-700 hover:bg-grey-100 hover:text-black px-4 py-2 no-underline'
				}
				key={item.key}
				to={item.path.replace(':propertyId', propertyId)}
			>
				{item.label}
			</Link>
		);
	}

	render() {
		const propertyId = this.props.match.params.propertyId;
		const navItems = this.getNavItems();
		const isKnownRouteProperty =
			!this.props.property.propertyList?.length ||
			this.props.property.propertyList.some(
				(property) => property.id === propertyId
			);
		const isPropertyReady =
			!isKnownRouteProperty ||
			(Boolean(propertyId) &&
				this.props.property.selectedProperty?.id === propertyId);

		return (
			<section className="mt-4">
				<section className="border-y border-grey-300 bg-white px-4 py-2 mb-6">
					<nav className="flex flex-wrap items-center gap-2">
						{navItems.map((item) =>
							this.renderNavLink(item, propertyId)
						)}
					</nav>
				</section>

				{this.state.isSyncingProperty || !isPropertyReady ? (
					<Loading />
				) : (
					this.props.children
				)}
			</section>
		);
	}
}

export default withTheme(withProperty(PropertyScopedLayout));
